import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Button, Flex, Form, Modal } from 'antd';
import type { TCreateTripTaskReq } from 'api/swagger';
import { TripFormSelect } from 'components/common/trip-form-select';
import { CoordinatesForm } from '../trip-create-task-modal/components/task-details/coordinates-form';
import { LocationSwitcher } from '../trip-create-task-modal/components/task-details/location-switcher';
import { SharedAddressFields } from '../trip-create-task-modal/components/task-details/shared-address-fields';
import type { TAddressFields, TCoordinatesFields } from '../trip-create-task-modal/types';
import { ELocationMode } from '../trip-create-task-modal/types';
import styles from './trip-trailer-swap-modal.module.scss';
import type { ITripTrailerSwapModalProps } from './types';
import { buildLocationPayload, validateTrailerSwapForm } from './utils';

const initialCoordinates: TCoordinatesFields = { lat: undefined, lng: undefined };
const initialAddress: TAddressFields = {
  name: undefined,
  countryId: undefined,
  postCode: undefined,
  city: undefined,
  address: undefined,
};

export const TripTrailerSwapModal = (props: ITripTrailerSwapModalProps) => {
  const { open, onCancel, onSubmit } = props;
  const { t } = useTranslation('trip');
  const { tripId } = useParams();
  const [form] = Form.useForm();
  const [locationMode, setLocationMode] = useState<ELocationMode>(ELocationMode.address);
  const [coordinates, setCoordinates] = useState<TCoordinatesFields>(initialCoordinates);
  const [address, setAddress] = useState<TAddressFields>(initialAddress);
  const [relatedTripId, setRelatedTripId] = useState<number | undefined>(undefined);

  const isValid = validateTrailerSwapForm(relatedTripId, locationMode, coordinates, address);

  const handleSubmit = async () => {
    const payload: TCreateTripTaskReq = {
      relatedTripId,
      ...buildLocationPayload(locationMode, coordinates, address),
    };

    await onSubmit(payload);
    setTimeout(() => {
      handleReset();
    }, 100);
  };

  const handleReset = () => {
    form.resetFields();
    setLocationMode(ELocationMode.address);
    setCoordinates(initialCoordinates);
    setAddress(initialAddress);
    setRelatedTripId(undefined);
  };

  const handleCancel = () => {
    handleReset();
    onCancel();
  };

  return (
    <Modal
      open={open}
      title={t('trailer_swap')}
      width={650}
      footer={
        <Flex justify='flex-end' gap='small'>
          <Button onClick={handleCancel}>{t('common:cancel')}</Button>
          <Button type='primary' disabled={!isValid} onClick={handleSubmit}>
            {t('common:save')}
          </Button>
        </Flex>
      }
      onCancel={handleCancel}>
      <Form form={form}>
        <Flex gap='large' vertical>
          <LocationSwitcher value={locationMode} onChange={setLocationMode} />

          {locationMode === ELocationMode.coordinates ? (
            <CoordinatesForm value={coordinates} onChange={setCoordinates} />
          ) : (
            <SharedAddressFields value={address} onChange={setAddress} />
          )}

          <TripFormSelect
            name='relatedTripId'
            label={t('trip')}
            handleChange={(option) => setRelatedTripId(option?.value)}
            wrapperClassName={styles.tripSelect}
            excludeTripId={Number(tripId)}
          />
        </Flex>
      </Form>
    </Modal>
  );
};
